import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { MessageCircleQuestion, RotateCcw, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { gradivostChat } from "@/api/client";
import type { GradivostChatResponse } from "@/api/schemas";
import { ExampleTeaser } from "@/features/feasibility/ExampleTeaser";
import { useGradivostChatStore } from "@/features/feasibility/useGradivostChatStore";
import { matchLimitsForQuestion } from "@/features/feasibility/verdictSession";

/**
 * EntryHero / AskBox — glavni (NL) ulaz modula gradivosti (`/provjera`).
 *
 * Pitanje → POST /api/gradivost/chat (parse + auto-zona) → roditelj hidratira
 * formu i, kad ništa ne nedostaje, okida provjeru. Kontekst čestice (chipovi,
 * zone, coverage, plan-CTA) NE živi ovdje nego u `ContextChips` (jedini izvor
 * istine). Prazno stanje = hero-naslov + primjeri + „Primjer" teaser.
 *
 * `showHistory` (= sesija aktivna) gejtira prikaz razgovora: svjež otvor
 * pokaže hero i kad je prošla sesija persistirana (čist start); „Prikaži"
 * ili prva interakcija je otkriju. Render-only — ne dira store/auto-run.
 */

const EXAMPLES = [
  "Što mogu graditi na čestici 706/1 k.o. Veprinac?",
  "Kuća Po+P+1 u zoni S1 u Opatiji, KIG 0,4",
  "Smijem li hotel u zoni T1 u Opatiji?",
];

export function Chip({
  children,
  tone = "stone",
  onClick,
  title,
}: {
  children: React.ReactNode;
  tone?: "stone" | "amber" | "ind";
  onClick?: () => void;
  title?: string;
}) {
  const tones = {
    stone: "border-stone-300 bg-stone-50 text-stone-700",
    amber: "border-amber-300 bg-amber-50 text-amber-800",
    ind: "border-ind-300 bg-ind-50 text-ind-700",
  } as const;
  const cls = `inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[0.78rem] ${tones[tone]} ${onClick ? "cursor-pointer hover:border-ind-400" : ""}`;
  return onClick ? (
    <button type="button" className={cls} onClick={onClick} title={title}>
      {children}
    </button>
  ) : (
    <span className={cls} title={title}>
      {children}
    </span>
  );
}

export function GradivostChat({
  onAnswer,
  engineRunning,
  verdict,
  onReset,
  showHistory = true,
}: {
  /** Roditelj hidratizira formu i po potrebi pokreće provjeru. */
  onAnswer: (resp: GradivostChatResponse, overrides?: { designation?: string }) => void;
  engineRunning: boolean;
  /** Verdikt zadnje provjere — chat ga veže uz razgovor kao „Odgovor: …". */
  verdict?: { label: string; kind: "ok" | "warn" | "err" } | null;
  /** Tvrdi reset sesije (razgovor + verdikt + parametri forme). */
  onReset?: () => void;
  /** Sesija aktivna → pokaži razgovor; false na svjež otvor (čist start). */
  showHistory?: boolean;
}) {
  const [text, setText] = useState("");
  const turns = useGradivostChatStore((s) => s.turns);
  const last = useGradivostChatStore((s) => s.lastResponse);
  const verdictSummary = useGradivostChatStore((s) => s.verdict);
  const addTurn = useGradivostChatStore((s) => s.addTurn);
  const clearSession = useGradivostChatStore((s) => s.clear);

  // P1-1: doslovan citat iz VEĆ izračunatog verdikta, pokazan odmah dok motor
  // (≈50 s) potvrđuje. Ne generira tekst — izdvaja stavku koja nosi citat.
  const [preview, setPreview] = useState<
    { question: string; rows: ReturnType<typeof matchLimitsForQuestion> } | null
  >(null);
  // P1-1: pitanje poslano dok motor radi ne gubi se — čeka u redu.
  const [queued, setQueued] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: (q: string) =>
      gradivostChat(q, useGradivostChatStore.getState().lastResponse?.parsed ?? null),
    onSuccess: (resp, q) => {
      addTurn(q, resp);
      setText("");
      onAnswer(resp);
    },
  });

  function run(q: string) {
    const v = q.trim();
    if (!v) return;
    if (ask.isPending || engineRunning) {
      setQueued(v);
      setText("");
      return;
    }
    const rows = matchLimitsForQuestion(
      v,
      useGradivostChatStore.getState().verdict?.conditions ?? [],
    );
    setPreview(rows.length ? { question: v, rows } : null);
    ask.mutate(v);
  }

  // Red: čim motor i parse stanu, pošalji pitanje koje je čekalo.
  const askRef = useRef(ask);
  askRef.current = ask;
  useEffect(() => {
    if (!queued || ask.isPending || engineRunning) return;
    const q = queued;
    setQueued(null);
    const rows = matchLimitsForQuestion(
      q,
      useGradivostChatStore.getState().verdict?.conditions ?? [],
    );
    setPreview(rows.length ? { question: q, rows } : null);
    askRef.current.mutate(q);
  }, [queued, ask.isPending, engineRunning]);

  // Instant citat živi točno do STVARNOG novog verdikta (checkedAt se mijenja
  // samo kad motor odgovori).
  const lastCheckedAt = useRef<string | null>(verdictSummary?.checkedAt ?? null);
  useEffect(() => {
    const now = verdictSummary?.checkedAt ?? null;
    if (now !== lastCheckedAt.current) {
      lastCheckedAt.current = now;
      setPreview(null);
    }
  }, [verdictSummary?.checkedAt]);

  const inConversation = turns.length > 0;
  // Čist start: prikaži razgovor samo kad je sesija aktivna (showHistory).
  const showConv = inConversation && showHistory;
  const lastTurn = turns[turns.length - 1];
  const verdictLine =
    showConv && verdict && !ask.isPending && !engineRunning && !lastTurn?.followupQuestion
      ? verdict
      : null;

  // P2: limit koji odgovara na ZADNJE pitanje, iz svježeg verdikta.
  const answerRows =
    verdictLine && lastTurn?.question
      ? matchLimitsForQuestion(lastTurn.question, verdictSummary?.conditions ?? [])
      : [];

  const VERDICT_TONES = {
    ok: "border-green-200 bg-green-50 text-green-800 hover:border-green-300",
    warn: "border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300",
    err: "border-red-200 bg-red-50 text-red-800 hover:border-red-300",
  } as const;

  return (
    <div className="mb-3 rounded-lg border border-ind-200 bg-ind-50/40 p-4" data-testid="gradivost-chat">
      {!showConv && (
        <p className="mb-2 text-[0.95rem] font-semibold text-stone-700">
          Što želiš graditi i gdje?
        </p>
      )}

      {showConv && (
        <div className="mb-3 space-y-1.5" data-testid="gradivost-chat-turns">
          {turns.map((t, i) => (
            <div key={i} className="text-[0.82rem]">
              <p className="text-stone-700">
                <span className="font-semibold text-stone-500">Ti:</span> {t.question}
              </p>
              {t.followupQuestion && i === turns.length - 1 && !ask.isPending && (
                <p className="mt-1 inline-flex items-start gap-1.5 rounded-md border border-ind-200 bg-white px-3 py-2 text-[0.85rem] text-stone-700">
                  <MessageCircleQuestion className="mt-0.5 h-4 w-4 shrink-0 text-ind-600" />
                  {t.followupQuestion}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {verdictLine && (
        <div className="mb-3">
          <button
            type="button"
            data-testid="gradivost-chat-verdict"
            className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-left text-[0.85rem] ${VERDICT_TONES[verdictLine.kind]}`}
            onClick={() =>
              document
                .querySelector('[data-testid="feasibility-result"]')
                ?.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            <span className="font-semibold">Odgovor: {verdictLine.label}</span>
            <span className="opacity-70">— pogledaj obrazloženje ↓</span>
          </button>
          {answerRows.length > 0 && (
            <ul className="mt-1.5 space-y-0.5" data-testid="gradivost-chat-answer">
              {answerRows.map((r, i) => (
                <li
                  key={i}
                  className="flex flex-wrap items-baseline gap-x-2 text-[0.82rem] text-stone-700"
                >
                  <span className="font-semibold">{r.name}</span>
                  <span>{r.value}</span>
                  {r.context && (
                    <span className="text-[0.75rem] text-stone-500">({r.context})</span>
                  )}
                  {r.source && <span className="text-[0.72rem] text-stone-400">— {r.source}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {preview && (
        <div
          data-testid="gradivost-chat-preview"
          className="mb-3 rounded-md border border-stone-200 bg-white px-3 py-2 text-[0.85rem]"
        >
          <p className="mb-1 text-[0.75rem] font-semibold uppercase tracking-[0.04em] text-stone-500">
            Iz prethodne provjere
          </p>
          <ul>
            {preview.rows.map((r, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-2 text-stone-700">
                <span className="font-semibold">{r.name}</span>
                <span>{r.value}</span>
                {r.context && <span className="text-[0.75rem] text-stone-500">({r.context})</span>}
                {r.source && <span className="text-[0.72rem] text-stone-400">— {r.source}</span>}
              </li>
            ))}
          </ul>
          <p className="mt-1 text-[0.75rem] text-stone-500">Potvrđujem za tvoj slučaj…</p>
        </div>
      )}

      {engineRunning && !ask.isPending && (
        <p className="mb-3 text-[0.8rem] text-stone-500">
          Provjeravam mogućnosti gradnje prema planu — obično do minute…
        </p>
      )}

      {queued && (
        <p className="mb-3 text-[0.8rem] text-stone-500" data-testid="gradivost-chat-queued">
          Sljedeće pitanje čeka: „{queued}" — šaljem ga čim provjera završi.
        </p>
      )}

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          run(text);
        }}
      >
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="flex-1 py-3 text-[0.92rem]"
          placeholder={
            last?.question && showConv
              ? "Odgovori (npr. 'k.o. Veprinac' ili 'kuću')…"
              : showConv
                ? 'Nastavi: "a KIG 0,5?", "a u zoni M1?"…'
                : "Pitaj o gradnji na svojoj čestici…"
          }
          aria-label="Pitanje o gradivosti"
        />
        <Button type="submit">
          <Send className="h-4 w-4" />
          {ask.isPending ? "Čitam…" : engineRunning ? "Dodaj pitanje" : "Pitaj"}
        </Button>
        {showConv && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setQueued(null);
              setPreview(null);
              setText("");
              if (onReset) onReset();
              else clearSession();
            }}
            title="Počni novi razgovor"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Nova provjera
          </Button>
        )}
      </form>

      {!showConv && !ask.isPending && (
        <>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                className="rounded-full border border-stone-200 bg-white px-2.5 py-0.5 text-[0.75rem] text-stone-500 hover:border-ind-300 hover:text-ind-700"
                onClick={() => run(ex)}
              >
                {ex}
              </button>
            ))}
          </div>
          <ExampleTeaser />
        </>
      )}

      {ask.isPending && (
        <p className="mt-2 text-[0.8rem] text-stone-500">
          Čitam — ako treba očitati zonu čestice iz službenog katastra, prvi put može potrajati
          do pola minute…
        </p>
      )}

      {ask.error instanceof Error && (
        <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[0.8rem] text-red-700">
          Greška: {ask.error.message}
        </p>
      )}
    </div>
  );
}
